/**
 * The 7am morning brief, as the Home card reads it.
 *
 * Types are written to the payload GET /api/v1/briefing/today ACTUALLY returns
 * (fieldiq-api `src/services/morning-brief.service.ts`, PR #698), verified
 * against live `morning_briefs` rows on 2026-09-11 — not to the generated API
 * manifest, which records this endpoint as a 500 because the manifest's schema
 * fixture is a pg_dump taken before migration 0398 existed. The endpoint is
 * healthy; the photograph is older than the table.
 *
 * EVERY NUMBER HERE IS THE API'S. Nothing in this file recomputes a figure the
 * brief already carries — the brief exists precisely so the card and the agent
 * cannot give two answers to one question.
 */
import { tenantCalendarDate } from './calendar-date';
import { OFFICE_TICKET_STATUS_LABELS, type TicketStatus } from './ticket-status';
import {
  BRIEF_TICKET_STATUSES,
  type BriefFacts,
  type BriefJob,
  type StoredBrief,
  type TicketStatusCounts,
} from '../types/morning-brief';

export { BRIEF_TICKET_STATUSES } from '../types/morning-brief';
export type {
  BriefFacts,
  BriefJob,
  BriefTicketStatus,
  StoredBrief,
  TicketStatusCounts,
} from '../types/morning-brief';

/**
 * ─── AGENT NAMES ────────────────────────────────────────────────────────────
 *
 * An exhaustive map over the whole `agent_runs.agent_name` CHECK vocabulary,
 * read from the live constraint on 2026-09-11 (12 values). Exhaustive and not
 * binary, per the ACCESS-column bug: a map that covers only the values someone
 * happened to see renders the rest as something a reader will mistake for data.
 *
 * Singular and plural are both spelled out rather than derived, because `s` is
 * wrong for enough English nouns that a rule would need exceptions anyway and
 * an exception list is the same table with extra steps.
 */
export const AGENT_LABELS: Record<string, { one: string; many: string }> = {
  clock_out_watch:       { one: 'clock-out check',        many: 'clock-out checks' },
  ot_threshold_guard:    { one: 'overtime alert',         many: 'overtime alerts' },
  anomaly_detector:      { one: 'anomaly',                many: 'anomalies' },
  timesheet_reminder:    { one: 'timesheet reminder',     many: 'timesheet reminders' },
  approval_nudge:        { one: 'approval nudge',         many: 'approval nudges' },
  stat_holiday_guard:    { one: 'stat holiday check',     many: 'stat holiday checks' },
  pay_day_prep:          { one: 'pay day item',           many: 'pay day items' },
  end_of_day_wrap:       { one: 'end-of-day wrap',        many: 'end-of-day wraps' },
  dispatch_suggestion:   { one: 'dispatch suggestion',    many: 'dispatch suggestions' },
  inspection_reminder:   { one: 'inspection reminder',    many: 'inspection reminders' },
  monthly_cost_analysis: { one: 'monthly cost review',    many: 'monthly cost reviews' },
  form_replication:      { one: 'form to replicate',      many: 'forms to replicate' },
};

/**
 * "50 inspection reminders". An agent name with no entry renders as
 * `2 unknown (some_new_agent)` — the count is never dropped and the raw value
 * is shown MARKED as unlabelled, following getJobTypeLabel's `Unknown (x)`.
 *
 * Echoing the raw value bare is what turns a display gap into something read
 * as data; hiding the row would silently under-report work that is waiting.
 */
export function agentRunLabel(agentName: string, count: number): string {
  const entry = AGENT_LABELS[agentName];
  if (!entry) return `${count} unknown (${agentName})`;
  return `${count} ${count === 1 ? entry.one : entry.many}`;
}

/**
 * Pending runs as label rows, largest group first.
 *
 * GROUPED, NEVER LISTED. `inspection_reminder` alone is 197 rows estate-wide
 * and 50 at Smith; a card that enumerated them would be the whole page.
 */
export function pendingAgentRows(
  byAgent: Record<string, number>,
): Array<{ agentName: string; count: number; label: string }> {
  return Object.entries(byAgent)
    .filter(([, n]) => typeof n === 'number' && n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([agentName, count]) => ({ agentName, count, label: agentRunLabel(agentName, count) }));
}

/**
 * The distinct jobs the period calls finished, in the order the brief lists them.
 *
 * completed_at and work_finished_at are two different EVENTS and a job can
 * carry both (the API's own worked example: PAR's J-0062, work-finished the
 * 8th and completed the 11th). Concatenating the two lists would show it
 * twice, and the count beside it — finishedJobCount, which the API computes as
 * a DISTINCT — would then disagree with the rows underneath.
 */
export function distinctFinishedJobs(period: BriefFacts['period']): BriefJob[] {
  const seen = new Set<string>();
  const out: BriefJob[] = [];
  for (const j of [...(period.jobsCompleted ?? []), ...(period.jobsWorkFinished ?? [])]) {
    if (!j || typeof j.jobId !== 'string' || seen.has(j.jobId)) continue;
    seen.add(j.jobId);
    out.push(j);
  }
  return out;
}

/**
 * "1 Draft · 2 Office Approved", or "No ticket yet".
 *
 * The trailing "N other" arm is the API's own disagreement signal made
 * visible: `ticketTotal` is a separate COUNT(*), so a status that exists in
 * the database without passing through the shared vocabulary is dropped from
 * `ticketCounts` and survives only in the total. Showing the remainder means a
 * new status appears as an unlabelled ticket rather than as a ticket that
 * silently stopped existing.
 */
export function ticketSummary(job: BriefJob): string {
  const counts = job.ticketCounts ?? ({} as TicketStatusCounts);
  const parts: string[] = [];
  let counted = 0;
  for (const s of BRIEF_TICKET_STATUSES) {
    const n = counts[s];
    if (typeof n !== 'number' || n <= 0) continue;
    counted += n;
    parts.push(`${n} ${OFFICE_TICKET_STATUS_LABELS[s as TicketStatus]}`);
  }
  const total = typeof job.ticketTotal === 'number' ? job.ticketTotal : counted;
  if (total <= 0) return 'No ticket yet';
  if (total > counted) parts.push(`${total - counted} other`);
  return parts.join(' · ');
}

/** True only when we can PROVE the brief is from an earlier tenant-local day. */
export function briefIsStale(brief: Pick<StoredBrief, 'briefDate' | 'timeZone'>, now: Date = new Date()): boolean {
  const today = tenantCalendarDate(brief.timeZone, now);
  if (!today || !brief.briefDate) return false;
  return brief.briefDate < today;
}

/**
 * ─── WHO THE BRIEF IS FOR ───────────────────────────────────────────────────
 *
 * ONE definition, read by the Home card and by the agent screen's opening
 * line. A second copy of "is this caller office?" is the shape the API's own
 * note warns about: it eventually disagrees with the endpoint, and it does so
 * in the direction of showing a company-wide brief to somebody the endpoint
 * would have refused.
 *
 * Narrower than the endpoint on purpose. GET /briefing/today is
 * `requireRole('admin','office_staff')` NON-strict, so the API would also
 * serve a dispatcher at a tenant with `dispatcher_admin_access`. That flag is
 * not on /auth/me — it needs a separate GET /settings/roles — and the audience
 * was decided as admin and office. Widening it later is a role read, not a
 * rewrite.
 */
export const BRIEF_ROLES = new Set(['admin', 'office_staff']);

/** True when this role may see the brief. An absent role is not an audience. */
export function isBriefAudience(role: unknown): boolean {
  return typeof role === 'string' && BRIEF_ROLES.has(role);
}
